/**
 * Fires e-commerce tracking events across all configured ad pixels.
 * Each function checks if the pixel's global object exists before calling.
 *
 * Supports: Meta Pixel (fbq), Google Tag (gtag), TikTok (ttq),
 *           Snapchat (snaptr), Pinterest (pintrk)
 */

import type { ContactChannel } from './contact-link';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    gtag?: (...args: any[]) => void;
    ttq?: { track: (...args: any[]) => void };
    snaptr?: (...args: any[]) => void;
    pintrk?: (...args: any[]) => void;
  }
}

type ProductEvent = {
  productId: string;
  name: string;
  price: number;
  currency: string;
  category?: string;
};

type AddToCartEvent = Omit<ProductEvent, 'category'> & { quantity?: number };

type PurchaseEvent = {
  orderId: string;
  value: number;
  currency: string;
  items: Array<{ id: string; name: string; price: number; quantity: number }>;
};

/** Multiplied and converted amounts carry float noise; ad platforms want cents. */
function money(amount: number): number {
  return Number(amount.toFixed(2));
}

/**
 * True the first time an order is seen in this browser. The confirmation page
 * stays reachable by URL, so a reload or a saved link would otherwise report the
 * sale again. If storage is blocked the order is still reported: a rare double
 * count costs less than losing the conversion.
 */
function isNewPurchase(orderId: string): boolean {
  const key = `uqhoan_purchase_tracked:${orderId}`;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, '1');
  } catch {
    // Storage unavailable.
  }
  return true;
}

/**
 * Track product page view (ViewContent)
 */
export function trackViewContent({ productId, name, price: rawPrice, currency }: ProductEvent) {
  const price = money(rawPrice);

  window.fbq?.('track', 'ViewContent', {
    content_ids: [productId],
    content_type: 'product',
    content_name: name,
    value: price,
    currency,
  });

  window.gtag?.('event', 'view_item', {
    currency,
    value: price,
    items: [{ item_id: productId, item_name: name, price }],
  });

  window.ttq?.track('ViewContent', {
    content_id: productId,
    content_type: 'product',
    content_name: name,
    value: price,
    currency,
  });

  window.snaptr?.('track', 'VIEW_CONTENT', {
    item_ids: [productId],
    price,
    currency,
  });

  window.pintrk?.('track', 'pagevisit', {
    line_items: [{ product_id: productId, product_name: name, product_price: price }],
  });
}

/**
 * Track add to cart event
 */
export function trackAddToCart({ productId, name, price, quantity = 1, currency }: AddToCartEvent) {
  const value = money(price * quantity);

  window.fbq?.('track', 'AddToCart', {
    content_ids: [productId],
    content_type: 'product',
    content_name: name,
    value,
    currency,
  });

  window.gtag?.('event', 'add_to_cart', {
    currency,
    value,
    items: [{ item_id: productId, item_name: name, price, quantity }],
  });

  window.ttq?.track('AddToCart', {
    content_id: productId,
    content_type: 'product',
    content_name: name,
    value,
    currency,
  });

  window.snaptr?.('track', 'ADD_CART', {
    item_ids: [productId],
    price: value,
    currency,
  });

  window.pintrk?.('track', 'addtocart', {
    value,
    currency,
    line_items: [{ product_id: productId, product_quantity: quantity }],
  });
}

/**
 * Track checkout initiation
 */
export function trackInitiateCheckout(rawValue: number, currency: string, numItems: number) {
  const value = money(rawValue);

  window.fbq?.('track', 'InitiateCheckout', { value, currency, num_items: numItems });
  window.gtag?.('event', 'begin_checkout', { currency, value });
  window.ttq?.track('InitiateCheckout', { value, currency });
  window.snaptr?.('track', 'START_CHECKOUT', { price: value, currency, number_items: numItems });
  window.pintrk?.('track', 'checkout', { value, currency });
}

/**
 * Track completed purchase
 */
export function trackPurchase({ orderId, value, currency, items }: PurchaseEvent) {
  if (!isNewPurchase(orderId)) return;

  // eventID covers what the browser check can't: the same order opened on
  // another device. It is also what a future server-side event would match on.
  window.fbq?.(
    'track',
    'Purchase',
    {
      content_ids: items.map((i) => i.id),
      content_type: 'product',
      value,
      currency,
      num_items: items.length,
    },
    { eventID: orderId },
  );

  window.gtag?.('event', 'purchase', {
    transaction_id: orderId,
    currency,
    value,
    items: items.map((i) => ({
      item_id: i.id,
      item_name: i.name,
      price: i.price,
      quantity: i.quantity,
    })),
  });

  window.ttq?.track('CompletePayment', {
    content_type: 'product',
    value,
    currency,
  });

  window.snaptr?.('track', 'PURCHASE', {
    price: value,
    currency,
    transaction_id: orderId,
    number_items: items.length,
  });

  window.pintrk?.('track', 'checkout', {
    value,
    currency,
    order_id: orderId,
    line_items: items.map((i) => ({
      product_id: i.id,
      product_quantity: i.quantity,
      product_price: i.price,
    })),
  });
}

let lastSearch: string | undefined;

/**
 * Track a search. One search can pass through here more than once (the overlay
 * reports it, then the results page it opens does), so a repeat is ignored.
 */
export function trackSearch(term: string) {
  if (term === lastSearch) return;
  lastSearch = term;

  window.fbq?.('track', 'Search', { search_string: term });
  window.gtag?.('event', 'search', { search_term: term });
  window.ttq?.track('Search', { query: term });
  window.snaptr?.('track', 'SEARCH', { search_string: term });
  window.pintrk?.('track', 'search', { search_query: term });
}

/**
 * Track a product saved to the wishlist
 */
export function trackAddToWishlist(productId: string) {
  window.fbq?.('track', 'AddToWishlist', { content_ids: [productId], content_type: 'product' });
  window.gtag?.('event', 'add_to_wishlist', { items: [{ item_id: productId }] });
  window.ttq?.track('AddToWishlist', { content_id: productId, content_type: 'product' });
  window.snaptr?.('track', 'ADD_TO_WISHLIST', { item_ids: [productId] });
}

/**
 * Track a visitor reaching out to the store
 */
export function trackContact(channel: ContactChannel) {
  window.fbq?.('track', 'Contact', { content_category: channel });
  window.gtag?.('event', 'contact', { method: channel });
  window.ttq?.track('Contact', { content_type: channel });
}

/**
 * Track a visitor leaving their details
 */
export function trackLead(source: 'newsletter' | 'contact_form') {
  window.fbq?.('track', 'Lead', { content_name: source });
  window.gtag?.('event', 'generate_lead', { lead_source: source });
  window.ttq?.track('SubmitForm', { content_name: source });
  window.pintrk?.('track', 'lead', { lead_type: source });
}

/**
 * Track a new account
 */
export function trackCompleteRegistration() {
  window.fbq?.('track', 'CompleteRegistration');
  window.gtag?.('event', 'sign_up', { method: 'email' });
  window.ttq?.track('CompleteRegistration');
  window.snaptr?.('track', 'SIGN_UP');
  window.pintrk?.('track', 'signup');
}

/**
 * Track a shopper choosing a product's options. Meta is the only pixel here
 * with a standard event for it.
 */
export function trackCustomizeProduct(productId: string, name: string) {
  window.fbq?.('track', 'CustomizeProduct', {
    content_ids: [productId],
    content_type: 'product',
    content_name: name,
  });
}
